import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import firstBy from 'thenby';

import {useBootcampConfigContext} from '@bootcamp/web/src/contexts/BootcampConfig';
import {useUserDataContext} from '@bootcamp/web/src/contexts/UserData';

import {getTestProgressByUserIdHashTestIdForScoring, getTest} from '@bootcamp/shared/src/requests';
import {scoreResult} from '@bootcamp/shared/src/util/scoring';
import {useDocumentTitle} from '@bootcamp/shared/src/util/hooks';

import {useMasteryArray} from '@bootcamp/shared/src/util/hooks';
import {formatClassroomMasteryWithTopic} from '@bootcamp/shared/src/util/formatting';
import {fetchAllQuestionStats, getSubjectTopicCountsForDATPerformance} from '../../pages/ScoreReport/adminStats';

import PageContainer, {Wrapper} from '@bootcamp/web/src/bootcamps/pages/PageContainer';
import {H1, H2, Body2} from '@bootcamp/web/src/components/Typography/next';
import Breadcrumbs from '@bootcamp/web/src/components/NavBar/components/Breadcrumbs';
import {Element} from '@bootcamp/web/src/bootcamps/components/WebflowFAQs';

import Test from './Test';
import Subject from './Subject';
import {PerformanceChart} from './PerformanceChart';
import TextToggle from '../../pages/ScoreReport/components/TextToggle';
import { TimeManagement } from './Time';


const Container = styled(PageContainer)`
  ${Wrapper} {
    padding-right: 0 !important;
    padding-left: 0 !important;
  }
`;
const ElementWrapper = styled.div`
  padding: 0px 32px;

  ${({theme}) => theme.mediaQueries.tablet} {
    padding: 0px 16px;
  }
`;

const Title = styled(H1)`
  color: white;
  margin-bottom: ${({theme}) => theme.layouts.spacing.s};
  `;
const SubTitle = styled(Body2)`
  color: ${({theme}) => theme.colors.typographyPalette.transparent};
  margin-bottom: ${({theme}) => theme.layouts.spacing.m};
  max-width: 750px;
`;
const StyledBreadcrumbs = styled(Breadcrumbs)`
  margin-bottom: ${({theme}) => theme.layouts.spacing.s};
`;
const SettingsArea = styled.div`
  display: flex;
  gap: ${({theme}) => theme.layouts.spacing.ms};
  margin-bottom: 48px;

  ${({theme}) => theme.mediaQueries.tablet} {
    padding: 0px;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const getTestsByClassroom = (classrooms, isBootcampPlusStudent) => classrooms
  .filter(({name}) => name !== 'Application Services')
  .reduce((acc, {route, contentTypes}) => ({
    ...acc,
    [route]: contentTypes
      .filter(({name}) => isBootcampPlusStudent ? ['Practice Tests', 'Plus Tests'].includes(name) : name === 'Practice Tests')
      .reduce((acc, {content}) => [
        ...acc,
        ...content
          .reduce((acc, {content}) => [
            ...acc,
            ...content
              ?.reduce((acc, {id}) => [...acc, id], []) || []
          ], [])
      ], [])
  }), {});

export const restoreQuizProgress = quizProgress => {
  const uniqueBlockProgresses = quizProgress.blockProgresses.items
    .sort(firstBy('index'))
    .reduce((acc, blockProgress) => {
      if (blockProgress.questionProgresses?.items.length === 0) return acc;
      if (!acc.find(filteredBlockProgress => filteredBlockProgress.index === blockProgress.index)) {
        acc.push(blockProgress);
      }
      return acc;
    }, []);

  return uniqueBlockProgresses.map(blockProgress => {
    const { questionProgresses: { items: questionProgresses } } = blockProgress;

    const restoredQuestionProgresses = questionProgresses.map((questionProgress, index) => {
      const question = questionProgress.question || {};
      const { subject, topic } = question.tags && question.tags.items && question.tags.items.length > 0 ? (question.tags.items.find(({ tag }) => tag && !!tag.subject && !!tag.topic) || {}).tag || {} : {};

      return {
        subject,
        topic,
        ...questionProgress,
      };
    });

    return {
      index: blockProgress.index,
      questions: restoredQuestionProgresses
    };
  });
};

const Performance = ({history}) => {
  const [userId, setUserId] = useState();

  const {config} = useBootcampConfigContext();
  const {isAdmin, isBootcampPlusStudent, DEFAULT_USER_ID, searchUserInteractions, saveUserInteraction, bootcamp} = useUserDataContext();

  useDocumentTitle(`${config.meta.siteTitle} | Performance`);

  const {sortOrder} = searchUserInteractions(`performance_order`, 'DESC');


  useEffect(() => {
    setUserId(DEFAULT_USER_ID);
  }, [DEFAULT_USER_ID]);

  const {fetching, dataByTest, dataBySubject, averages, testAverages, subjectAverages, subjectTopicCounts, mastery, refetch} = usePerformance(isBootcampPlusStudent, config, bootcamp, searchUserInteractions, userId, saveUserInteraction);

  const handleUpdateSortOrder = () => {
    const newSortOrder = sortOrder === 'ASC' ? 'DESC' : 'ASC';
    saveUserInteraction(`performance_order`, JSON.stringify({sortOrder: newSortOrder}));
    refetch(newSortOrder);
  };

  return (
    <Container>
      <ElementWrapper>
        <StyledBreadcrumbs history={history} lockAtDepth={2} />
        <Title onClick={() => isAdmin && setUserId(window.prompt('Enter user ID to spoof') || DEFAULT_USER_ID)}>Performance</Title>
        <SubTitle>Your performance reflects results from your completed practice tests only. Results from question banks or bites are not included.</SubTitle>
      </ElementWrapper>
      <ElementWrapper>
        <SettingsArea>
          <TextToggle
            options={[
              {text: 'First Attempt', value: 'ASC'},
              {text: 'Latest Attempt', value: 'DESC'}
            ]}
            defaultOptionValue={sortOrder}
            onChange={handleUpdateSortOrder}
            lightMode
          />
        </SettingsArea>
      </ElementWrapper>
      <Test
        dataByTest={dataByTest}
        fetching={fetching}
        sortOrder={sortOrder}
        averages={averages}
      />
      <Subject dataBySubject={dataBySubject} subjectTopicCounts={subjectTopicCounts} mastery={mastery}/>
      <PerformanceChart averages={averages} dataByTest={dataByTest} fetching={fetching} />
      <TimeManagement dataBySubject={dataBySubject} expandAll={false} subjectTopicCounts={subjectTopicCounts} mastery={mastery}/>
      <ElementWrapper>
        <H2 style={{margin: '32px 0px 16px', color: 'white'}}>FAQs</H2>
        <Element title="How is my performance calculated?" text="<p>Your performance is based on the results of your completed practice tests.</p>" />
        <Element title="Can I reset my performance?" text="<p>Unfortunately, you cannot reset your performance. However, you can take each practice test as many times as you’d like. Select Show latest attempt to only see your most recent attempt for each practice test.</p>" />
        <Element title="Can I reset or untag my tagged questions?" text="<p>Unfortunately, you cannot reset or untag your tagged questions. Once a question is tagged, it remains tagged. However, you can review every question as many times as you'd like and change the tag each time.</p>" />
      </ElementWrapper>
    </Container>
  )
}

const getAndFormatAverages = async (testIds) => {
  if (!testIds?.length) return null;

  try {
    const stats = await testIds.reduce(async (acc, id) => {
      const testStats = await fetchAllQuestionStats(id, []);
      const totals = testStats?.reduce((acc, curr) => {
        let update = acc;

        update.avgScore += curr?.avgScore;
        update.avgTime += curr?.avgTime;

        return update;
      }, {avgScore: 0, avgTime: 0});

      const averages = {
        avgScore: (totals?.avgScore / testStats?.length) || .66,
        avgTime: (totals?.avgTime / testStats?.length) || 0
      };

      const awaitedAcc = await acc;

      return {...awaitedAcc, [id]: averages};
    }, {});
    return stats;
  } catch (error) {
    return '';
  }
};

const formatTestMastery = (masteryObj) => {
  return Object.values(masteryObj).reduce((acc, {byTopic}) => {
    let update = acc;

    Object
      .values(byTopic)
      .forEach(topic => {
        const {topic: topicName, subject: subjectName} = topic?.tag || {};

        if (!topic) return;

        const {learning, reviewing, mastered, total} = topic;

        update[subjectName] = {
          ...(update[subjectName] || {}),
          learning: (update[subjectName]?.learning || 0) + (learning || 0),
          reviewing: (update[subjectName]?.reviewing || 0) + (reviewing || 0),
          mastered: (update[subjectName]?.mastered || 0) + (mastered || 0),
          total: (update[subjectName]?.total || 0) + (total || 0),
          topics: {
            ...(update[subjectName]?.topics || {}),
            [topicName]: {
              ...(update[subjectName]?.topics?.[topicName] || {}),
              learning: (update[subjectName]?.topics?.[topicName]?.learning || 0) + (learning || 0),
              reviewing: (update[subjectName]?.topics?.[topicName]?.reviewing || 0) + (reviewing || 0),
              mastered: (update[subjectName]?.topics?.[topicName]?.mastered || 0) + (mastered || 0),
              total: (update[subjectName]?.topics?.[topicName]?.total || 0) + (total || 0),
            }
          }
        };
      })

    return update;
  }, {})
};

export const usePerformance = (isBootcampPlusStudent, config, bootcamp, searchUserInteractions, DEFAULT_USER_ID, saveUserInteraction) => {
  const [fetching, setFetching] = useState(false);
  const [testAverages, setTestAverages] = useState({});
  const [subjectTopicCounts, setSubjectTopicCounts] = useState({});

  const {sortOrder} = searchUserInteractions(`performance_order`, {sortOrder: 'DESC'});
  const performanceRecord = searchUserInteractions(`${bootcamp}_performance`);
  const SUBJECTS = ['Biology', 'General Chemistry', 'Organic Chemistry', 'Perceptual Ability', 'Reading Comprehension', 'Quantitative Reasoning']
  const TESTS = Array.from({ length: isBootcampPlusStudent ? 15 : 10 }, (_, i) => (i + 1).toString());

  const testsByClassroom = getTestsByClassroom(config.classrooms, isBootcampPlusStudent);
  const practiceTestIds = Object.entries(testsByClassroom).reduce((acc, [classroom, testIds]) => [...acc, ...testIds], []);
  const fltIds = config.studyTools[0].content.map(({id}) => id);

  // TODO check to see if we need to add plus tests here...
  const testMastery = useMasteryArray([...practiceTestIds, ...fltIds], 'test', data => formatClassroomMasteryWithTopic(data, 'test'), null, null, DEFAULT_USER_ID, !DEFAULT_USER_ID);
  const formattedTestMastery = formatTestMastery(testMastery);

  const dataByTest = TESTS.map(testNumber => {
    const subjectResults = SUBJECTS.map(testSubject => {
      const record = performanceRecord?.results?.find(({testSubject: ts, testNumber: tn}) => ts === testSubject && tn === testNumber);

      if (!record) return {
        score: '-'
      };


      const {testId, testProgressId, score, createdAt} = record;

      return {score, testId, testProgressId, date: createdAt, testNumber, subject: testSubject};
    });

    const filteredSubjectResults = subjectResults.filter(({score}, index) => score != '-' && index !== 3);
    const getAcademicAverage = (subjectArray) => {
      const average = Math.round(subjectArray.reduce((acc, {score}) => acc + parseFloat(score), 0) / subjectArray.length);

      return Math.round(average / 10) * 10;
    };
    const academicAverage = filteredSubjectResults.length === 5
      ? getAcademicAverage(filteredSubjectResults)
      : '-';
    return [...subjectResults, {score: academicAverage}]
  })

  const dataBySubject = SUBJECTS.map(testSubject => {
    return TESTS.reduce((acc, testNumber) => {
      const record = performanceRecord?.results?.find(({testSubject: ts, testNumber: tn}) => ts === testSubject && tn === testNumber);
      if (!record) return acc;
      return record.topics.filter(({name}) => !['Geometry'].includes(name)).reduce((acc, {name, correct, total, timeSpent}) => {
        return {
          testSubject,
          total: acc.total + total,
          correct: acc.correct + correct,
          timeSpent: acc.timeSpent + parseFloat(timeSpent),
          topics: {
            ...acc.topics,
            [name]: acc.topics[name] ? {
              total: acc.topics[name].total + total,
              correct: acc.topics[name].correct + correct,
              timeSpent: acc.topics[name].timeSpent + parseFloat(timeSpent)
            } : {
              total,
              correct,
              timeSpent: parseFloat(timeSpent)
            }
          }
        }
      }, acc);
    }, {testSubject, total: 0, correct: 0, timeSpent: 0.0, topics: {}})
  });

  const averages = dataByTest?.[0].map((_, index) => {
    // transpose breakdown
    const subjectScores = dataByTest.map(test => test[index]).filter(({score}) => score !== '-');
    if (!subjectScores.length) return {content: '-'}
    const subjectAverage = subjectScores.reduce((acc, {score}) => acc + parseInt(score), 0) / subjectScores.length;

    return {
      content: Math.round(subjectAverage / 10) * 10
    }
  });

  // const averagesBySubject = dataByTest
  //   ?.flat()
  //   ?.reduce((acc, {subject, testId}) => {
  //     let update = acc;

  //     update[subject]
  //       ? update[subject].push(testAverages[testId])
  //       : update[subject] = [testAverages[testId]];

  //     return update;
  //   }, {});

  // const subjectAverages = Object.keys(averagesBySubject || {}).reduce((acc, subject) => {
  //   const totals = averagesBySubject?.[subject]?.reduce((acc, curr) => {
  //     const avgScore = curr?.avgScore || 0;
  //     const avgTime = curr?.avgTime || 0;

  //     let update = acc;

  //     update.avgScore += avgScore;
  //     update.avgTime += avgTime;

  //     return update;
  //   }, {avgScore: 0, avgTime: 0});

  //   acc[subject] = {
  //     avgScore: totals.avgScore / averagesBySubject[subject].length,
  //     avgTime: totals.avgTime / averagesBySubject[subject].length
  //   }
  //   return acc;
  //   }, {});

  const fetch = async (sortOrder) => {
    if (fetching) return;

    try {
      setFetching(true);

      const allTestIds = [...practiceTestIds, ...fltIds];

      // const testAverages = await getAndFormatAverages(allTestIds);
      const subjectTopicCounts = await getSubjectTopicCountsForDATPerformance(config);

      // setTestAverages(testAverages);
      setSubjectTopicCounts(subjectTopicCounts);

      if (performanceRecord.createdAt && performanceRecord.sortOrder === sortOrder) {
        return;
      }

      performanceRecord.createdAt = new Date().toISOString();
      performanceRecord.results = [];
      performanceRecord.sortOrder = sortOrder;

      const testFetchResults = await Promise.all(allTestIds.map(testId => getTestProgressByUserIdHashTestIdForScoring(`${DEFAULT_USER_ID}#${testId}`, true, sortOrder)));
      const testResults = testFetchResults.map(({ data }) => data.TestProgressByTestId.items[0]).filter(a => !!a);
      const tests = await Promise.all(testResults.map(({ testId }) => getTest(testId, 'getTestConfig')));
      const testsById = tests.reduce((acc, { data }) => ({ ...acc, [data.getTest.id]: data.getTest }), {});

      const statsReducer = (acc, quizProgress) => {
        const testConfig = testsById[quizProgress.testId];
        const testTitle = testConfig.config.title;
        const testSubject = testTitle.match(/([^#]+)( Test)/)[1];
        const testNumber = testTitle.match(/#(.*)/)[1];

        const restoredProgress = restoreQuizProgress(quizProgress);

        const stats = fltIds.includes(quizProgress.testId)
          ? SUBJECTS.map((subject, index) => {
              const testTitle = `${subject} Test #${testNumber}`;
              const startIndex = index >= 5 ? index + 2 : index;
              const endIndex = index >= 4 ? index + 3 : index + 1;
              const result = restoredProgress.slice(startIndex, endIndex);
              const stats = scoreResult(result, testTitle, subject, testNumber, quizProgress.createdAt);
              return { stats: { ...stats, testProgressId: quizProgress.id, testId: quizProgress.testId } };
            })
          : [{ stats: { ...scoreResult(restoredProgress, testTitle, testSubject, testNumber, quizProgress.createdAt), testProgressId: quizProgress.id, testId: quizProgress.testId } }];

        return [...acc, ...stats];
      };

      const testStats = testResults.reduce(statsReducer, []);

      for (const { stats } of testStats) {
        const matchingRecord = performanceRecord.results.find(({ testSubject, testNumber }) => testSubject === stats.testSubject && testNumber === stats.testNumber);
        if (!matchingRecord) {
          performanceRecord.results.push(stats);
        } else {
          if ((sortOrder === 'DESC' && matchingRecord.createdAt < stats.createdAt) || (sortOrder === 'ASC' && matchingRecord.createdAt > stats.createdAt)) {
            performanceRecord.results = performanceRecord.results.filter(({ testSubject, testNumber }) => !(testSubject === stats.testSubject && testNumber === stats.testNumber));
            performanceRecord.results.push(stats);
          }
        }
      }

      await saveUserInteraction(`${bootcamp}_performance`, JSON.stringify(performanceRecord));
    } catch (error) {
      console.log(error);
    } finally {
      setFetching(false);
    }
  };

    // this should only ever refetch on first load, on sortOrder change, and on scoring mode change.
  useEffect(() => {
    if (!DEFAULT_USER_ID) return;

    fetch(sortOrder);

  }, [DEFAULT_USER_ID]);

  return {dataByTest, dataBySubject, averages, testAverages, subjectTopicCounts, mastery: formattedTestMastery, fetching, refetch: fetch};
}

export default Performance;